import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import mixins from '../../assets/styles/mixins.scss';
import CommonStyles from '../../assets/styles/CommonStyles';
import tickIcon from '../../assets/images/tickIcon.svg';
import Icons from '../icons/Icon';
import { getDataAttributes, dataAttributesPropType } from '../../common/dataAttributes';

const DYNAMIC_TOGGLE_LABEL_TYPOGRAPHY = CommonStyles().dynamicToggleLabel;

const ToggleContainer = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  }
});

const ToggleLabel = styled(Typography)({
  '&&': DYNAMIC_TOGGLE_LABEL_TYPOGRAPHY,
  '&&&.MuiTypography-body1': DYNAMIC_TOGGLE_LABEL_TYPOGRAPHY
});

const ToggleWrapper = styled('div')({
  '&&': {
    padding: 5,
    borderRadius: 20,
    position: 'relative',
    cursor: 'pointer'
  }
});

const ToggleTrack = styled('div')({
  '&&': {
    width: 52,
    height: 24,
    transition: 'background-color 0.3s, border-color 0.3s',
    display: 'flex',
    alignItems: 'center'
  }
});

const ToggleThumb = styled('div')({
  '&&': {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    transition: 'left 0.3s, background-color 0.3s',
    textAlign: 'center'
  }
});

const CheckMark = styled('span')({
  '&&': {
    width: 16,
    height: 16,
    display: 'contents'
  }
});

const CrossMark = styled('span')({
  '&&': {
    position: 'absolute',
    right: 10,
    width: 16,
    height: 16,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
});

const CustomToggle = ({
  label, value, onChange, dataAttributes
}) => {
  const trackStyle = {
    justifyContent: value ? 'flex-end' : 'space-between'
  };

  const wrapperStyle = {
    backgroundColor: value ? mixins.amethystColor : mixins.chalkWhiteFont,
    border: `1px solid ${value ? mixins.amethystColorV2 : mixins.amethystColor}`
  };

  const thumbStyle = {
    backgroundColor: value ? mixins.chalkWhiteFont : mixins.amethystColor
  };

  return (
    <ToggleContainer>
      <ToggleLabel>{label}</ToggleLabel>
      <ToggleWrapper style={wrapperStyle}>
        <ToggleTrack
          style={trackStyle}
          role="switch"
          aria-checked={value}
          {...getDataAttributes(dataAttributes)}
          tabIndex={0}
          onClick={() => onChange(!value)}
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              onChange(!value);
            }
          }}
        >
          {!value &&
          <CrossMark>
            <Icons name="CloseIcon" />
          </CrossMark>
          }
          <ToggleThumb style={thumbStyle}>
            {value &&
            <CheckMark>
              <img alt="" src={tickIcon} />
            </CheckMark>
          }
          </ToggleThumb>
        </ToggleTrack>
      </ToggleWrapper>
    </ToggleContainer>
  );
};

CustomToggle.propTypes = {
  label: PropTypes.string,
  value: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  dataAttributes: dataAttributesPropType
};

CustomToggle.defaultProps = {
  label: 'Show checkbox',
  dataAttributes: {}
};

export default CustomToggle;
